import { useState } from 'react';
import { Settings, X } from 'lucide-react';
import { Button } from './ui/button';
import { API_BASE_URL, DEMO_MODE } from '../lib/api';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';

export function DevSettings() {
  const [isOpen, setIsOpen] = useState(false);

  // Panel tylko w development mode
  if (!import.meta.env.DEV) {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-12 h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-50"
        title="Developer Settings"
      >
        <Settings className="w-5 h-5" />
      </button>

      {/* Settings Panel */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">Developer Settings</h2>
                <p className="text-sm text-slate-600">Konfiguracja API i trybu pracy</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Current Configuration */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <h3 className="font-semibold text-slate-900 mb-3">Aktualna Konfiguracja</h3>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Tryb:</span>
                    <Badge variant={DEMO_MODE ? "outline" : "default"}>
                      {DEMO_MODE ? 'Demo Mode' : 'Production Mode'}
                    </Badge>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">API URL:</span>
                    <code className="text-xs bg-slate-200 px-2 py-1 rounded">
                      {API_BASE_URL}
                    </code>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Environment:</span>
                    <Badge variant="outline">
                      {import.meta.env.MODE}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Jak zmienić konfigurację?</h3>
                
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
                  <h4 className="font-medium text-blue-900 text-sm">1. Połącz się z serwerem VPS</h4>
                  <p className="text-sm text-blue-800">
                    Edytuj plik <code className="bg-blue-100 px-1.5 py-0.5 rounded">.env</code> w głównym katalogu:
                  </p>
                  <pre className="bg-slate-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto">
{`# Ustaw URL swojego VPS
VITE_API_URL=https://api.twoj-serwer.com

# Wyłącz tryb demo
VITE_DEMO_MODE=false`}
                  </pre>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                  <h4 className="font-medium text-amber-900 text-sm">2. Testuj lokalnie</h4>
                  <p className="text-sm text-amber-800">
                    Jeśli masz backend uruchomiony lokalnie:
                  </p>
                  <pre className="bg-slate-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto">
{`VITE_API_URL=http://localhost:3000
VITE_DEMO_MODE=false`}
                  </pre>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
                  <h4 className="font-medium text-slate-900 text-sm">3. Tryb Demo (domyślny)</h4>
                  <p className="text-sm text-slate-700">
                    Aplikacja używa mock data - nie wymaga backendu:
                  </p>
                  <pre className="bg-slate-900 text-green-400 p-3 rounded-lg text-xs overflow-x-auto">
{`VITE_API_URL=http://localhost:3000
VITE_DEMO_MODE=true`}
                  </pre>
                </div>
              </div>

              {/* Important Note */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <h4 className="font-medium text-red-900 text-sm mb-2">⚠️ Ważne!</h4>
                <p className="text-sm text-red-800">
                  Po zmianie pliku <code className="bg-red-100 px-1 py-0.5 rounded">.env</code> musisz 
                  zrestartować serwer Vite (Ctrl+C, potem ponownie uruchom aplikację).
                </p>
              </div>

              {/* Quick Links */}
              <div className="pt-4 border-t border-slate-200">
                <h4 className="font-medium text-slate-900 text-sm mb-3">Dokumentacja</h4>
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => {
                      // W prawdziwej aplikacji możemy otworzyć dokumentację
                      alert('Zobacz plik VPS_SETUP.md w głównym katalogu projektu');
                    }}
                  >
                    📖 VPS Setup Guide
                  </Button>
                  <Button
                    variant="outline"
                    className="justify-start"
                    onClick={() => {
                      console.log('Environment Variables:', {
                        VITE_API_URL: import.meta.env.VITE_API_URL,
                        VITE_DEMO_MODE: import.meta.env.VITE_DEMO_MODE,
                        MODE: import.meta.env.MODE,
                        DEV: import.meta.env.DEV,
                        PROD: import.meta.env.PROD,
                      });
                      alert('Sprawdź konsolę przeglądarki');
                    }}
                  >
                    🔍 Show All Env Variables
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
